import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import { Button, Panel, plural } from '../ds';
import { localDay, monthGrid } from './news-utils';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

export interface CalendarItem {
  id: string;
  title: string;
  publishedAt: string | null;
  requiresAck: boolean;
  isRead: boolean;
}

/**
 * Календарь публикаций (порт идеи CRM NewsCalendarTab): месячная сетка,
 * опубликованные публикации ленты по дню выхода. Запланированные видит
 * только УК в CRM — здесь их нет (лента HUB отдаёт только опубликованное).
 */
export function NewsCalendar({ items, onOpen }: { items: CalendarItem[]; onOpen: (id: string) => void }): JSX.Element {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const today = localDay(now.toISOString());
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarItem[]>();
    for (const it of items) {
      if (!it.publishedAt) continue;
      const k = localDay(it.publishedAt);
      m.set(k, [...(m.get(k) ?? []), it]);
    }
    return m;
  }, [items]);
  const grid = monthGrid(cursor.y, cursor.m);
  const inMonth = grid.filter((c) => c.inMonth).reduce((s, c) => s + (byDay.get(c.day)?.length ?? 0), 0);
  const shift = (d: number): void =>
    setCursor((c) => {
      const m = c.m + d;
      return { y: c.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
    });

  return (
    <Panel
      title={`${MONTHS[cursor.m]} ${cursor.y}`}
      sub={`${inMonth} ${plural(inMonth, 'публикация', 'публикации', 'публикаций')} · оранжевым — нужно подтвердить`}
      pad={0}
      right={
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <Button size="sm" variant="ghost" icon={<LeftOutlined />} aria-label="Предыдущий месяц" onClick={() => shift(-1)} />
          <Button size="sm" variant="ghost" onClick={() => setCursor({ y: now.getFullYear(), m: now.getMonth() })}>
            Сегодня
          </Button>
          <Button size="sm" variant="ghost" icon={<RightOutlined />} aria-label="Следующий месяц" onClick={() => shift(1)} />
        </span>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {WEEKDAYS.map((d) => (
          <div key={d} style={{ padding: '8px 10px', fontSize: 12, fontWeight: 600, color: 'var(--fk-text-muted)', background: 'var(--fk-table-head)' }}>
            {d}
          </div>
        ))}
        {grid.map((c) => {
          const evs = byDay.get(c.day) ?? [];
          return (
            <div
              key={c.day}
              style={{
                minHeight: 92,
                padding: 6,
                borderTop: '1px solid var(--fk-row-line)',
                borderLeft: '1px solid var(--fk-row-line)',
                background: c.inMonth ? 'var(--fk-surface)' : 'var(--fk-bg-subtle)',
                minWidth: 0,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <b style={{ color: c.inMonth ? 'var(--fk-text)' : 'var(--fk-text-muted)' }}>{Number(c.day.slice(8))}</b>
                {c.day === today && <span style={{ color: 'var(--fk-orange)', fontWeight: 700 }}>сегодня</span>}
              </div>
              {evs.slice(0, 3).map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => onOpen(e.id)}
                  title={e.title}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    font: 'inherit',
                    fontSize: 11.5,
                    lineHeight: 1.3,
                    padding: '3px 6px',
                    marginBottom: 3,
                    borderRadius: 4,
                    border: 'none',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    background: e.requiresAck && !e.isRead ? 'var(--fk-orange-soft)' : 'var(--fk-info-soft)',
                    color: e.requiresAck && !e.isRead ? 'var(--fk-orange)' : 'var(--fk-text)',
                  }}
                >
                  {e.title}
                </button>
              ))}
              {evs.length > 3 && <div style={{ fontSize: 11, color: 'var(--fk-text-muted)' }}>ещё {evs.length - 3}</div>}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
